/**
 * Damerau-Levenshtein edit distance (optimal string alignment).
 *
 * Plain Levenshtein counts a transposition as two edits, so `nkie` would sit
 * distance 2 from `nike` and slip past a threshold of 1. Typo-squatting is
 * mostly transpositions, so the transposition-aware variant is the one worth
 * having. Inputs here are at most a few dozen characters, so the O(n*m) table
 * is not worth optimizing.
 */
export function damerauLevenshtein(a: string, b: string): number {
  if (a === b) return 0
  if (a.length === 0) return b.length
  if (b.length === 0) return a.length

  // (a.length + 1) x (b.length + 1) table, row-major.
  const width = b.length + 1
  const d = new Uint16Array((a.length + 1) * width)

  for (let i = 0; i <= a.length; i += 1) d[i * width] = i
  for (let j = 0; j <= b.length; j += 1) d[j] = j

  for (let i = 1; i <= a.length; i += 1) {
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      let best = Math.min(
        (d[(i - 1) * width + j] ?? 0) + 1, // deletion
        (d[i * width + (j - 1)] ?? 0) + 1, // insertion
        (d[(i - 1) * width + (j - 1)] ?? 0) + cost, // substitution
      )
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        best = Math.min(best, (d[(i - 2) * width + (j - 2)] ?? 0) + 1) // transposition
      }
      d[i * width + j] = best
    }
  }

  return d[a.length * width + b.length] ?? 0
}

/**
 * Edit-distance threshold for a brand of the given length.
 *
 * Short brands need a tight threshold: at distance 1, four-letter words like
 * `visa` sit next to ordinary English (`vise`, `visas`, `vista`), so allowing
 * 2 would block legitimate names constantly.
 */
export function thresholdFor(brandLength: number): number {
  if (brandLength <= 4) return 0
  if (brandLength <= 7) return 1
  return 2
}
